/**
 * One team tile on the home page (#2640).
 *
 * Header: team name, role chip, grade chip and a cast character whose pose
 * shows the state. Body: the two-week desk calendar. Footer: the requests
 * ring (lead) or "Requests sent" (member) on the left, the needs-you chips or
 * "All clear" on the right.
 *
 * The tile is not one big link: the team name carries a stretched link (its
 * `::after` covers the card) and the calendar cells, chips and the delete
 * button sit above it as their own hit areas, so none of them nests inside
 * another link and each is reachable by keyboard.
 *
 * A team with no overview row (old API, failed call) still renders its header.
 * A tasks team has no calendar and no ring.
 */
import { Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

import { Button, Skeleton } from "../../components/ui";
import { cn } from "../../lib/cn";
import { type TeamKind, resolveTeamHomeRoute } from "../../lib/teamApps";
import { MiniCalendar } from "./MiniCalendar";
import { NeedsChips, RequestsRing, RequestsSent } from "./TileParts";
import {
  type MemberLeave,
  type MemberShift,
  castFor,
  chipHref,
  currentCycleId,
  gradeChipOf,
  leadWeeks,
  memberWeeks,
  needChips,
  poseFor,
  roleLabel,
} from "./homeOverview";
import type { TeamOverviewRow } from "./overviewTypes";

export interface HomeTileTeam {
  id: string;
  name: string;
  role: string;
  team_kind?: string | null;
}

export interface TeamTileProps {
  team: HomeTileTeam;
  row: TeamOverviewRow | undefined;
  /** The overview call is still running: a calendar placeholder shows. */
  loading: boolean;
  monday: string;
  today: string;
  memberFacts: { assignments: MemberShift[]; leaves: MemberLeave[] };
  onDelete?: (() => void) | undefined;
}

export function TeamTile({
  team,
  row,
  loading,
  monday,
  today,
  memberFacts,
  onDelete,
}: TeamTileProps) {
  const isTasks = team.team_kind === "tasks";
  const isLead = team.role === "lead" || team.role === "co_lead";
  const isMember = team.role === "member";
  const showFacts = !isTasks && row !== undefined;

  const stateWeeks = row ? leadWeeks(monday, today, row.cycles ?? [], team.id) : [];
  const calendarWeeks =
    row && isMember
      ? memberWeeks(monday, today, team.id, memberFacts)
      : row
        ? leadWeeks(monday, today, row.cycles ?? [], team.id)
        : [];
  const chips = isTasks ? [] : needChips(team.role, row);
  const needsCount = chips.reduce((sum, c) => sum + c.count, 0);
  const pose = isTasks || !row ? "idle" : poseFor(needsCount, stateWeeks);
  const cast = castFor(team.id);

  const cycleId = currentCycleId(today, row?.cycles);
  const leaveCycleId = row?.needs_you?.leave_cycle_id ?? null;
  const swapCycleId = row?.needs_you?.swap_cycle_id ?? null;
  const grade = gradeChipOf(row);
  const homeHref = resolveTeamHomeRoute((team.team_kind ?? "scheduling") as TeamKind, team.id);

  const requests = row?.requests ?? null;
  const left =
    isLead && requests ? (
      <RequestsRing submitted={requests.submitted} total={requests.total} />
    ) : isMember && requests?.mine_sent ? (
      <RequestsSent />
    ) : null;

  return (
    <article
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-bg-elevated transition-colors focus-within:border-accent hover:border-accent/40"
      data-testid={`home-team-tile-${team.id}`}
      data-tour="team-tile"
      data-pose={pose}
    >
      <div className="h-[104px] bg-accent-soft px-[18px] py-4 [background-image:radial-gradient(80%_140%_at_75%_20%,rgb(var(--color-bg-elevated)/0.55),transparent_70%)]">
        <h3 className="max-w-[62%] text-[17px] font-semibold leading-tight text-fg">
          <Link
            to={homeHref}
            className="line-clamp-2 after:absolute after:inset-0 after:z-0 focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-accent"
          >
            {team.name}
          </Link>
        </h3>
        <div className="mt-2 flex max-w-[64%] flex-wrap gap-1.5">
          <span
            className={cn(
              "inline-flex items-center rounded-full border px-2.5 py-0.5 text-[12.5px]",
              isLead
                ? "border-accent bg-accent text-fg-on-accent"
                : "border-border bg-bg-elevated text-fg-muted",
            )}
            data-testid="home-role-chip"
          >
            {roleLabel(team.role)}
          </span>
          {grade ? (
            <span
              className="inline-flex items-center rounded-full border border-border bg-bg-elevated px-2.5 py-0.5 text-[12.5px] text-fg-muted"
              data-testid="home-grade-chip"
            >
              {grade}
            </span>
          ) : null}
        </div>
      </div>
      <img
        src={`/site/characters/${cast}/${pose}.svg`}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute right-9 top-0 h-[104px] select-none"
        data-testid="home-tile-character"
      />
      {team.role === "lead" && onDelete ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-1 top-1 z-10 h-7 w-7 text-fg-faint hover:text-danger"
          aria-label={`Delete ${team.name}`}
          onClick={onDelete}
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </Button>
      ) : null}

      {isTasks ? null : showFacts ? (
        <>
          <div className="flex-1 px-[18px] pb-3.5 pt-4">
            <MiniCalendar weeks={calendarWeeks} />
          </div>
          <div className="flex min-h-[60px] items-center justify-between gap-3 border-t border-border px-[18px] py-3">
            <div>{left}</div>
            <div className="ml-auto">
              <NeedsChips
                chips={chips}
                hrefFor={(chip) =>
                  chipHref(team.id, chip.kind, chip.count, {
                    cycleId,
                    leaveCycleId,
                    swapCycleId,
                    member: isMember,
                  })
                }
              />
            </div>
          </div>
        </>
      ) : loading ? (
        <div className="flex-1 px-[18px] pb-4 pt-4">
          <Skeleton className="h-[104px]" />
        </div>
      ) : null}
    </article>
  );
}
